<template>
  <p class="nowrap">
    {{ formattedDate }}
  </p>
</template>

<script lang="ts" setup>
import moment from 'moment';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  date: string
}>();

const formattedDate = ref('');
const intervalId = ref<NodeJS.Timeout | null>(null);

const { locale } = useI18n(); // Теперь вызывается в `setup()`

function isValidDate(dateString: string) {
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime());
}

function getFormattedDate(timestamp: string, locale: string): string {
  const updateDate = moment(
    isValidDate(timestamp) ? new Date(timestamp) : +timestamp,
  );
  const now = moment();
  const diffSeconds = now.diff(updateDate, 'seconds');
  const diffMinutes = now.diff(updateDate, 'minutes');
  const diffHours = now.diff(updateDate, 'hours');
  const diffDays = now.diff(updateDate, 'days');

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });

  if (diffSeconds < 60) {
    return rtf.format(-diffSeconds, 'second');
  } else if (diffMinutes < 60) {
    return rtf.format(-diffMinutes, 'minute');
  } else if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  } else {
    return rtf.format(-diffDays, 'day');
  }
}

function getUpdateInterval(dateStr: string): number {
  const timestamp = new Date(dateStr).getTime();
  if (Number.isNaN(timestamp))
    return 3600000;

  const diff = Date.now() - timestamp;
  if (diff < 60000)
    return 1000; // Обновлять каждую секунду, если < 1 минуты
  if (diff < 3600000)
    return 60000; // Обновлять каждую минуту, если < 1 часа
  return 3600000; // Обновлять каждый час
}

function updateTime() {
  if (!props.date)
    return;

  try {
    formattedDate.value = getFormattedDate(props.date, locale.value);
  } catch (error) {
    console.error('Error formatting date:', error);
    formattedDate.value = props.date; // Показываем дату как есть в случае ошибки
  }

  // Очищаем старый интервал перед установкой нового
  if (intervalId.value)
    clearInterval(intervalId.value);

  // Устанавливаем новый интервал с актуальным значением
  intervalId.value = setInterval(updateTime, getUpdateInterval(props.date));
}

onMounted(updateTime);

onBeforeUnmount(() => {
  if (intervalId.value)
    clearInterval(intervalId.value);
});
</script>

<style>
</style>
